import { useEffect, useMemo, useRef, useState } from "react";
import { HLS_STREAMING } from "src/api/featureFlags";
import parseV0TemplateUrl from "src/helpers/parseV0TemplateUrl";
import getHlsJs from "src/helpers/videoHelpers/getHlsJs.web";
import { PhotoDisplayContentProps } from "../../types";

const PhotoDisplayVideoContent = ({
	photo,
	photoWidth,
	photoHeight,
	onLoad,
	onDetailedLoad,
	isActive,
}: PhotoDisplayContentProps) => {
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const imageUrl =
		parseV0TemplateUrl({
			readUrl: photo.readUrl,
			strategy: "inside",
			size: "lg",
		}) ?? undefined;
	const videoUrl =
		(HLS_STREAMING
			? parseV0TemplateUrl({
					readUrl: photo.readUrl,
					auto: "allowStream",
				})
			: parseV0TemplateUrl({
					readUrl: photo.readUrl,
					direct: "stable-video",
				})) ??
		// Keep stableVideoUrl for optimistic behaviors
		photo.readUrl?.stableVideoUrl;

	// There's a short period of time where the video display unsupported content until HLS takes over
	// We just don't display during that time
	const waitingForHls = useMemo(() => {
		if (!videoUrl) return false;
		return videoUrl.includes(".m3u8") && HLS_STREAMING;
	}, [videoUrl]);
	const [shouldDisplay, setShouldDisplay] = useState(false);
	useEffect(() => {
		if (waitingForHls) return;
		const timeout = setTimeout(() => {
			setShouldDisplay(true);
		}, 50);
		return () => clearTimeout(timeout);
	}, [waitingForHls]);

	useEffect(() => {
		if (!isActive) {
			videoRef.current?.pause();
		}
	}, [isActive]);

	useEffect(() => {
		if (!videoUrl || !waitingForHls) return;
		const video = videoRef.current;
		if (!video) return;
		(async () => {
			const Hls = await getHlsJs();
			if (Hls.isSupported()) {
				const hls = new Hls();
				hls.loadSource(videoUrl);
				hls.attachMedia(video);
				/*
			    hls.on(Hls.Events.MANIFEST_PARSED, () => {
			    	video.play();
			    });
                */
			} else if (video.canPlayType("application/vnd.apple.mpegurl")) {
				video.src = videoUrl;
				/*
			    video.addEventListener("loadedmetadata", () => {
			    	video.play();
			    });
                */
			}
			setShouldDisplay(true);
		})();
	}, [videoUrl, waitingForHls]);
	if (!videoUrl) return null;

	return (
		<video
			ref={videoRef}
			src={videoUrl}
			controls
			poster={imageUrl}
			crossOrigin="anonymous"
			onLoadedData={() => {
				onLoad?.({
					success: true,
				});
				onDetailedLoad?.({
					success: true,
				});
			}}
			onError={() => {
				onLoad?.({
					success: false,
				});
			}}
			style={{
				width: photoWidth,
				height: photoHeight,
				objectFit: "contain",
				opacity: shouldDisplay ? 1 : 0,
				transition: "opacity 0.2s",
			}}
		/>
	);
};
export default PhotoDisplayVideoContent;
